/**
 * shared/events.ts —— 全局事件委托的【唯一入口】
 * 来历：一期 [SEC:events] 段的思路，但组织方式改了一处。
 *
 * ## 与一期的差别
 * 一期是一个巨大的 document click 监听，里面按 data-* 分支到七个页面的处理函数，
 * 到后期这一段接近 190 行、每加一个模块就往里塞几个分支。
 * 二期改成：**全局只认「跨页面的三类」，其余一律转发给当前页模块的 onEvent。**
 *   跨页面三类 = 弹窗（.mask / [data-mclose]）、导航（[data-nav]）、退出登录（[data-lg]）
 *
 * 好处：新增模块【不用改这个文件】，页面卸载也不用解绑（监听只有这一份，永远在 document 上）。
 *
 * ⚠ 一条纪律：**页面模块里不许自己 addEventListener 到 document**。
 *   一期没有这个问题（单文件、没有卸载），二期页面会来回切换，
 *   在页面里绑 document 而不解绑 = 切十次页就有十个监听在跑，且都持有旧数据的闭包。
 *   要监听就用委托：给元素加 data-* 标记，在本页的 onEvent 里认。
 */
import { closeModal, hasModal } from './common.js';
import { closeAssistant, openAssistant } from './assistant.js';

/** 当前页的事件处理器，由 router/render.ts 在每次渲染后替换 */
type PageHandler = ((e: Event) => void) | null;
let pageHandler: PageHandler = null;
export function setPageHandler(h: PageHandler): void {
  pageHandler = h;
}

/* ===================== 离开页面的拦截（W2-6 新增） =====================
   起因：双周报编辑页有用户原创的长文本，而**二期不保存草稿**（草稿归三期）——
   离开就丢。一期在全局 click 的 [data-nav] 分支上包了一层 rpGuard，二期收成这一处钩子：
   `router/index.ts` 的 go() 会先问它一句，所以【侧栏、面包屑、页内跳转全都被覆盖】。

   ⚠ 三条纪律：
   1. **只有编辑态才设它**。列表页与详情页是纯只读，拦截只会碍事（09 方案 §三）。
   2. **每次渲染后自动清空**（`router/render.ts` 在 mounted 之前调 setLeaveGuard(null)），
      否则从编辑页走掉之后，守卫会留在那儿继续拦别的页面的跳转。
   3. **浏览器后退键拦不住**（那要改写 hash 才能挡，代价是把后退栈搅乱）——
      一期同样拦不住，这是已知边界，不是漏做。 */
type LeaveGuard = ((proceed: () => void) => void) | null;
let leaveGuard: LeaveGuard = null;

/** 设置 / 清除离开拦截。⚠ 传 null 清除；页面卸载时 render 会自动清 */
export function setLeaveGuard(g: LeaveGuard): void {
  leaveGuard = g;
}

/** 走一次离开拦截：没设守卫就直接放行 */
export function guardLeave(proceed: () => void): void {
  if (leaveGuard) leaveGuard(proceed);
  else proceed();
}

/** 由 main.ts 注入，避免 shared 反向依赖 router / api */
type Actions = {
  go: (hash: string) => void;
  logout: () => void;
  retry: () => void;
};
let act: Actions = { go: () => {}, logout: () => {}, retry: () => {} };
export function setActions(a: Actions): void {
  act = a;
}

/** 手机端导航抽屉开合。桌面端 `.mnav-btn` 不显示，这个类挂上也没有任何样式命中 */
function setMobileNav(on: boolean): void {
  document.body.classList.toggle('mnav-on', on);
  const btn = document.querySelector('.mnav-btn');
  btn?.setAttribute('aria-expanded', String(on));
  btn?.setAttribute('aria-label', on ? '收起导航' : '展开导航');
}

export function installEvents(): void {
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    /* 手机端导航抽屉（2026-09-14，只在 ≤767px 可见，见 `31_mobile.css`）。
       ⚠ 侧栏里点任何菜单 / 退出登录都要先收起，否则跳转后抽屉还盖在新页面上。 */
    const mnav = t.closest<HTMLElement>('[data-mnav]');
    if (mnav) return setMobileNav(mnav.dataset.mnav === 'toggle' && !document.body.classList.contains('mnav-on'));
    if (t.closest('.sidenav')) setMobileNav(false);

    /* 弹窗内的点击交给弹窗自己处理；⚠ 点遮罩不关闭，防误触丢失填写内容（一期已定的交互） */
    if (t.closest('.mask')) {
      if (t.closest('[data-mclose]')) closeModal(false);
      return;
    }

    /* AI 助手抽屉（2026-08-24）。⚠ 抽屉内部的点击由 `shared/assistant.ts` 自己监听，
       这里**只放行 `[data-nav]`**（回答里的队伍名要能跳转），其余一律不劫持——
       否则 pageHandler 会收到一堆抽屉内的事件，当前页要为一个不属于它的浮层写分支。 */
    if (t.closest('#aid')) {
      const link = t.closest<HTMLElement>('[data-nav]');
      if (!link) return;
      /* ⚠ 窄屏（≤640px）下抽屉是**满屏**的（`25_assistant.css` 的媒体查询），
         点了队伍名会跳转到抽屉背后——用户看到的是"点了没反应"。宽屏则要保持打开：
         「一边看矩阵一边问」正是这个功能不加遮罩的理由。 */
      if (window.matchMedia('(max-width:640px)').matches) closeAssistant();
      return act.go(link.dataset.nav as string);
    }
    if (t.closest('[data-ai]')) return openAssistant();

    if (t.closest('[data-retry]')) return act.retry();

    const nav = t.closest<HTMLElement>('[data-nav]');
    if (nav) return act.go(nav.dataset.nav as string);

    if (t.closest('[data-lg="logout"]')) return act.logout();

    pageHandler?.(e);
  });

  /* input / change 只转发给当前页：一期的筛选、搜索、进度快调都走这两个事件 */
  document.addEventListener('input', (e) => pageHandler?.(e));
  document.addEventListener('change', (e) => pageHandler?.(e));
  /* focusout 是打卡与快报的就地编辑提交时机（一期 2026-08-04 加的），保留 */
  document.addEventListener('focusout', (e) => pageHandler?.(e));
  /* submit 用捕获阶段监听：submit 事件【不冒泡到 document 之外】，但会冒泡到 document；
     用捕获是为了保证页面 preventDefault 一定跑得到，否则表单会真的提交、整页刷新。 */
  document.addEventListener('submit', (e) => pageHandler?.(e), true);

  document.addEventListener('keydown', (e) => {
    /* ⚠ 弹窗优先于抽屉：抽屉里也可能开着弹窗（复制失败的 toast 不算），
       先关最上面那一层才是符合直觉的顺序 */
    if (e.key === 'Escape' && document.body.classList.contains('mnav-on')) {
      setMobileNav(false);
      return;
    }
    if (e.key === 'Escape' && !hasModal() && document.body.classList.contains('aid-on')) {
      closeAssistant();
      return;
    }
    if (e.key === 'Escape' && hasModal()) {
      // ⚠ 有未保存改动时 closeModal 会先弹二次确认，不要在这里直接 dropModal
      closeModal(false);
      return;
    }
    pageHandler?.(e);
  });

  window.addEventListener('resize', (e) => pageHandler?.(e));

  /* 图片取不到时露出底下的占位格（2026-08-23 图片提速）。
     🔴 **必须用捕获阶段**：`error` 事件**不冒泡**，挂在 document 上的普通监听收不到它，
       整页会留着一格一格的破图图标。
     ⚠ 只处理带 `data-imgfb` 的 `<img>`：它的约定是「同一个盒子里已经有一个占位元素垫在下面」，
       所以这里直接把 img 摘掉就行，不用再造 DOM。
     ⚠ 会走到这条分支的正常情况：COS 开通前存的记录（只有键没有对象）、
       本机开发不配密钥（`/api/img` 恒 404）、对象被误删。**这些都不是异常，不要弹 toast。** */
  document.addEventListener(
    'error',
    (e) => {
      const t = e.target;
      if (t instanceof HTMLImageElement && t.hasAttribute('data-imgfb')) t.remove();
    },
    true,
  );
}
